import { useEffect, useState } from 'react';
import './App.css';
import LoadingIndicator from './LoadingIndicator';
import Login from './Login';
import Register from './Register';
import TaskList from './tasklist';
import Dashboard from './Dashboard';
import ChecklistDashboard from './ChecklistDashboard';
import DelegationDashboard from './DelegationDashboard';
import FMSDashboard from './FMSDashboard';
import ReturnReverseLogisticsFMS from './ReturnReverseLogisticsFMS';
import DelegationTasks from './delegation_tasks';
import ResourceControl from './ResourceControl';
import AllTasks from './AllTasks';
import Logging from './Logging';

const VIEW_PATHS = {
  tasks: '/tasks',
  dashboard: '/dashboard',
  fms: '/fms',
  returns: '/returns',
  delegation: '/delegation',
  allTasks: '/all-tasks',
  logging: '/logging',
  resourceControl: '/resource-control',
  register: '/register',
};

function getViewFromPath(pathname) {
  switch (pathname) {
    case '/dashboard':
      return 'dashboard';
    case '/fms':
      return 'fms';
    case '/returns': 
      return 'returns';
    case '/delegation':      // ✅ ADDED THIS CASE
      return 'delegation';   // ✅ ADDED THIS RETURN
    case '/all-tasks':
      return 'allTasks';
    case '/logging':
      return 'logging';
       case '/resource-control':  // ADD THIS
      return 'resourceControl';  // ADD THIS
    case '/register':
      return 'register';
    case '/':
    case '/tasks':
    default:
      return 'tasks';
  }
}



function getAuthViewFromPath(pathname) {
  return pathname === '/register' ? 'register' : 'login';
}

function getPathFromView(view) {
  return VIEW_PATHS[view] || '/tasks';
}

function App() {
  const initialPath = window.location.pathname;

  const [authView, setAuthView] = useState(getAuthViewFromPath(initialPath));
  const [session, setSession] = useState({ user: null, accessToken: '' });
  const [checkingSession, setCheckingSession] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [currentView, setCurrentView] = useState(getViewFromPath(initialPath));

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const res = await fetch('/home', { credentials: 'include' });
        if (!res.ok) return;

        const data = await res.json();
        if (!cancelled) {
          setSession({ user: data.user, accessToken: '' });
        }
      } catch {
        // No active cookie/session; the login screen will be shown.
      } finally {
        if (!cancelled) {
          setCheckingSession(false);
        }
      }
    }

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      setCurrentView(getViewFromPath(path));
      setAuthView(getAuthViewFromPath(path));
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToView = (view) => {
    const path = getPathFromView(view);
    setCurrentView(view);
    window.history.pushState({}, '', path);
  };

  const navigateToAuthView = (view) => {
    const path = view === 'register' ? '/register' : '/login';
    setAuthView(view);
    window.history.pushState({}, '', path);
  };

  async function handleLogout() {
    setLoggingOut(true);

    try {
      await fetch('/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setSession({ user: null, accessToken: '' });
      setAuthView('login');
      setCurrentView('tasks');
      window.history.pushState({}, '', '/login');
      setLoggingOut(false);
    }
  }

  if (checkingSession) {
    return (
      <main className="auth-loading">
        <LoadingIndicator variant="page" label="Checking your session..." />
      </main>
    );
  }

  if (session.user) {
    if (currentView === 'returns') {
      return (
        <ReturnReverseLogisticsFMS
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToFMS={() => navigateToView('fms')}
          onNavigateToReturns={() => navigateToView('returns')}
          onNavigateToDelegation={() => navigateToView('delegation')}
           onNavigateToAllTasks={() => navigateToView('allTasks')}
           onNavigateToResourceControl={() => navigateToView('resourceControl')}
           onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'fms') {
      return (
        <FMSDashboard
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToFMS={() => navigateToView('fms')}
          onNavigateToReturns={() => navigateToView('returns')}
          onNavigateToDelegation={() => navigateToView('delegation')}
           onNavigateToAllTasks={() => navigateToView('allTasks')}
           onNavigateToResourceControl={() => navigateToView('resourceControl')}
           onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'dashboard') {
      return (
        <Dashboard
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToChecklistDashboard={() => setCurrentView('checklist_dashboard')}
            onNavigateToDelegationDashboard={() => setCurrentView('delegation_dashboard')}
        onNavigateToFMS={() => navigateToView('fms')}
        onNavigateToReturns={() => navigateToView('returns')} 
        onNavigateToRegister={() => navigateToView('register')}
        onNavigateToDelegation={() => navigateToView('delegation')}
        onNavigateToAllTasks={() => navigateToView('allTasks')}
        onNavigateToResourceControl={() => navigateToView('resourceControl')}  // ADD THIS
        onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'checklist_dashboard') {
      return (
        <ChecklistDashboard
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToChecklistDashboard={() => setCurrentView('checklist_dashboard')}
            onNavigateToDelegationDashboard={() => setCurrentView('delegation_dashboard')}
        onNavigateToFMS={() => navigateToView('fms')}
        onNavigateToReturns={() => navigateToView('returns')} 
        onNavigateToRegister={() => navigateToView('register')}
        onNavigateToDelegation={() => navigateToView('delegation')}
        onNavigateToAllTasks={() => navigateToView('allTasks')}
        onNavigateToResourceControl={() => navigateToView('resourceControl')}  // ADD THIS
        onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'delegation_dashboard') {
      return (
        <DelegationDashboard
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToChecklistDashboard={() => setCurrentView('checklist_dashboard')}
          onNavigateToDelegationDashboard={() => setCurrentView('delegation_dashboard')}
          onNavigateToFMS={() => navigateToView('fms')}
          onNavigateToReturns={() => navigateToView('returns')}
          onNavigateToRegister={() => navigateToView('register')}
          onNavigateToDelegation={() => navigateToView('delegation')}
          onNavigateToAllTasks={() => navigateToView('allTasks')}
          onNavigateToResourceControl={() => navigateToView('resourceControl')}
          onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'delegation') {
      return (
        <DelegationTasks
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToFMS={() => navigateToView('fms')}         // ✅ PASSED PROP
          onNavigateToReturns={() => navigateToView('returns')} // ✅ PASSED PROP
           onNavigateToAllTasks={() => navigateToView('allTasks')}
           onNavigateToResourceControl={() => navigateToView('resourceControl')}
           onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'allTasks') {
      return (
        <AllTasks
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToFMS={() => navigateToView('fms')}
          onNavigateToReturns={() => navigateToView('returns')}
          onNavigateToDelegation={() => navigateToView('delegation')}
          onNavigateToAllTasks={() => navigateToView('allTasks')}
          onNavigateToResourceControl={() => navigateToView('resourceControl')}
          onNavigateToRegister={() => navigateToView('register')}
          onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'logging') {
      return (
        <Logging
          user={session.user}
          accessToken={session.accessToken}
          onAuthError={handleLogout}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToFMS={() => navigateToView('fms')}
          onNavigateToReturns={() => navigateToView('returns')}
          onNavigateToDelegation={() => navigateToView('delegation')}
          onNavigateToAllTasks={() => navigateToView('allTasks')}
          onNavigateToResourceControl={() => navigateToView('resourceControl')}
          onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    // Add to the return statement (before the final return for TaskList):
if (currentView === 'resourceControl') {
      return (
        <ResourceControl
          user={session.user}
          accessToken={session.accessToken}
          onLogout={handleLogout}
          loggingOut={loggingOut}
          onNavigateToDashboard={() => navigateToView('dashboard')}
          onNavigateToTasks={() => navigateToView('tasks')}
          onNavigateToFMS={() => navigateToView('fms')}
          onNavigateToReturns={() => navigateToView('returns')}
          onNavigateToDelegation={() => navigateToView('delegation')}
          onNavigateToAllTasks={() => navigateToView('allTasks')}
          onNavigateToResourceControl={() => navigateToView('resourceControl')}
          onNavigateToRegister={() => navigateToView('register')}
          onNavigateToLogging={() => navigateToView('logging')}
        />
      );
    }

    if (currentView === 'register') {
      return (
        <Register 
          onShowLogin={() => navigateToView('tasks')} 
        />
      );
    }

    // Default fallback is TaskList
    return (
      <TaskList
        user={session.user}
        accessToken={session.accessToken}
        onAuthError={handleLogout}
        onLogout={handleLogout}
        loggingOut={loggingOut}
        onNavigateToDashboard={() => navigateToView('dashboard')}
        onNavigateToTasks={() => navigateToView('tasks')}
        onNavigateToFMS={() => navigateToView('fms')}
        onNavigateToReturns={() => navigateToView('returns')} 
        onNavigateToRegister={() => navigateToView('register')}
        onNavigateToDelegation={() => navigateToView('delegation')}
        onNavigateToAllTasks={() => navigateToView('allTasks')}
        onNavigateToResourceControl={() => navigateToView('resourceControl')}  // ADD THIS
        onNavigateToLogging={() => navigateToView('logging')}
      />
    );
  }

  if (authView === 'register') {
    return <Register onShowLogin={() => navigateToAuthView('login')} />;
  }

  return (
    <Login
      onLogin={(nextSession) => setSession(nextSession)}
      onShowRegister={() => navigateToAuthView('register')}
    />
  );
}

export default App;
